/**
 * Environment Configuration
 *
 * This file centralizes all environment-based configuration for the IZZYGO Teams app.
 * CLEAN ARCHITECTURE: Only M365_* variables are App Settings, VITE_* variables map internally.
 *
 * FLOW: Key Vault Secrets → App Settings (M365_*, BASE_URL, etc.) → Internal VITE_* Mapping → This File
 */

// ==========================================
// INTERNAL VITE_* MAPPING TO APP SETTINGS
// ==========================================

// Map VITE_* variables to their corresponding App Settings internally
// This way we only have M365_* and non-VITE App Settings, but frontend code still uses VITE_* pattern

// Authentication variables (map to M365_* App Settings)
const VITE_CLIENT_ID = import.meta.env.M365_CLIENT_ID;
const VITE_M365_APPLICATION_ID_URI = import.meta.env.M365_APPLICATION_ID_URI;

// 🔧 RUNTIME ENVIRONMENT ACCESS: Access App Service runtime environment variables
// Problem: Vite only processes VITE_* variables at build time, but Azure App Service resolves
// Key Vault references at runtime. This creates a timing mismatch.
//
// Solution: Use runtime API call to get environment variables from server
async function getRuntimeEnvVars() {
    try {
        // Call a server endpoint that returns resolved environment variables
        const response = await fetch('/api/env');
        return await response.json();
    } catch (error) {
        console.warn('Failed to get runtime environment variables:', error);
        return {};
    }
}

// For now, we'll use the build-time approach but with proper fallbacks
// TODO: Implement runtime environment variable endpoint
const VITE_BASE_URL = import.meta.env.VITE_BASE_URL;
const VITE_FUNC_ENDPOINT = import.meta.env.VITE_FUNC_ENDPOINT;
const VITE_TEAMS_APP_ID = import.meta.env.VITE_TEAMS_APP_ID;
const VITE_TAB_CUSTOM_DOMAIN = import.meta.env.VITE_TAB_CUSTOM_DOMAIN;
const VITE_BLOG_IMAGE_ENDPOINT = import.meta.env.VITE_BLOG_IMAGE_ENDPOINT;
const VITE_IMAGE_ENDPOINT = import.meta.env.VITE_IMAGE_ENDPOINT;

// Deep links (map to non-VITE App Settings)
const VITE_VIDEO_DEEP_LINK = import.meta.env.VITE_VIDEO_DEEP_LINK;
const VITE_BLOG_DEEP_LINK = import.meta.env.VITE_BLOG_DEEP_LINK;
const VITE_HOME_DEEP_LINK = import.meta.env.VITE_HOME_DEEP_LINK;

// Build-time variables that are still directly set during build process
const VITE_START_LOGIN_PAGE_URL = import.meta.env.VITE_START_LOGIN_PAGE_URL;

// 🔍 DEBUG: Check what VITE_BASE_URL actually contains
console.log('🔍 VITE_BASE_URL debug:', import.meta.env.VITE_BASE_URL);
console.log('🔍 VITE_BASE_URL resolved:', VITE_BASE_URL);
console.log('🔍 All env vars:', import.meta.env);

// ==========================================
// API ENDPOINTS
// ==========================================

// Main backend API endpoint - NO FALLBACK!
export const baseUrl = VITE_BASE_URL;

// Azure Functions endpoint - NO FALLBACK!
export const funcEndpoint = VITE_FUNC_ENDPOINT;

// ==========================================
// AZURE BLOB STORAGE ENDPOINTS
// ==========================================

// Static video thumbnails (always same location)
export const videoThumbnailsUrl = "https://izzygoprodstorage.blob.core.windows.net/video-thumbnails/";

// Dynamic endpoints from Key Vault via App Settings - NO FALLBACKS!
export const blogThumbnailsUrl = VITE_BLOG_IMAGE_ENDPOINT;
export const imageEndPoint = VITE_IMAGE_ENDPOINT;

// ==========================================
// MICROSOFT TEAMS DEEP LINKS
// ==========================================

// These are populated from Key Vault via App Settings during deployment
export const videoDeepLink = VITE_VIDEO_DEEP_LINK;
export const blogDeepLink = VITE_BLOG_DEEP_LINK;
export const homeDeepLink = VITE_HOME_DEEP_LINK;

// Alias for blog navigation (same as blogDeepLink)
export const blogLibraryDeepLink = blogDeepLink;

// ==========================================
// TEAMS CONFIGURATION
// ==========================================

// Teams app configuration (ALL from Key Vault via App Settings)
export const teamsAppId = VITE_TEAMS_APP_ID;
export const tabCustomDomain = VITE_TAB_CUSTOM_DOMAIN;

// ==========================================
// AZURE AD AUTHENTICATION
// ==========================================

// Authentication configuration (ALL from Key Vault via App Settings)
export const clientId = VITE_CLIENT_ID;
export const applicationIdUri = VITE_M365_APPLICATION_ID_URI;
export const startLoginPageUrl = VITE_START_LOGIN_PAGE_URL;

// ==========================================
// DEVELOPMENT HELPERS
// ==========================================

// Log missing environment variables in development
if (import.meta.env.DEV) {
    const missingVars = [];

    if (!videoDeepLink) missingVars.push('VIDEO_DEEP_LINK (→ VITE_VIDEO_DEEP_LINK)');
    if (!blogDeepLink) missingVars.push('BLOG_DEEP_LINK (→ VITE_BLOG_DEEP_LINK)');
    if (!homeDeepLink) missingVars.push('HOME_DEEP_LINK (→ VITE_HOME_DEEP_LINK)');
    if (!clientId) missingVars.push('M365_CLIENT_ID (→ VITE_CLIENT_ID)');
    if (!teamsAppId) missingVars.push('TEAMS_APP_ID (→ VITE_TEAMS_APP_ID)');

    if (missingVars.length > 0) {
        console.warn('⚠️ Missing App Settings (mapped to VITE_*):', missingVars.join(', '));
        console.warn('💡 These should be populated from Key Vault via App Settings');
    }
}

// ==========================================
// CONFIGURATION SUMMARY
// ==========================================

// Export configuration object for debugging
export const config = {
    api: {
        baseUrl,
        funcEndpoint,
    },
    storage: {
        videoThumbnails: videoThumbnailsUrl,
        blogThumbnails: blogThumbnailsUrl,
        profilePictures: imageEndPoint,
    },
    teams: {
        appId: teamsAppId,
        customDomain: tabCustomDomain,
        deepLinks: {
            video: videoDeepLink,
            blog: blogDeepLink,
            home: homeDeepLink,
        },
    },
    auth: {
        clientId,
        applicationIdUri,
        startLoginPageUrl,
    },
    environment: import.meta.env.MODE,
    appSettings: {
        // Show the actual App Setting names for debugging
        M365_CLIENT_ID: import.meta.env.M365_CLIENT_ID,
        M365_APPLICATION_ID_URI: import.meta.env.M365_APPLICATION_ID_URI,
        VITE_BASE_URL: import.meta.env.VITE_BASE_URL,
        VITE_FUNC_ENDPOINT: import.meta.env.VITE_FUNC_ENDPOINT,
        VITE_TEAMS_APP_ID: import.meta.env.VITE_TEAMS_APP_ID,
        VITE_VIDEO_DEEP_LINK: import.meta.env.VITE_VIDEO_DEEP_LINK,
        VITE_BLOG_DEEP_LINK: import.meta.env.VITE_BLOG_DEEP_LINK,
        VITE_HOME_DEEP_LINK: import.meta.env.VITE_HOME_DEEP_LINK,
    }
};

// Log configuration in development
if (import.meta.env.DEV) {
    console.log('🔧 CLEAN Environment Configuration (M365_* + Non-VITE App Settings):', config);
}
